import { FormItemProps } from "antd";
import type { FieldError, FieldErrors, UseControllerProps } from "react-hook-form";
import { getRequiredErrorMessage } from "./getRequiredErrorMessage";

export type FormRules<TFieldValues = unknown> = Omit<UseControllerProps<TFieldValues>["rules"], "required">;

type ValidationReturn = {
  isRequired?: boolean;
  validate?: FormItemProps["validateStatus"];
  validMessage?: string;
  rules?: UseControllerProps["rules"];
};

function isFieldError(error: unknown): error is FieldError {
  return (
    typeof error === "object" && error !== null && "message" in error && typeof (error as any).message === "string"
  );
}

function getNestedError(obj: unknown, path: string): unknown {
  return path.split(".").reduce((acc, key) => {
    if (acc && typeof acc === "object" && key in acc) {
      return (acc as any)[key];
    }
    return undefined;
  }, obj);
}

type GetValidationPropsOptions<TFieldValues> = {
  fieldName: string;
  errors?: FieldErrors<TFieldValues>;
  isRequired?: boolean;
  includeFieldNameInError?: boolean;
  rules?: FormRules<TFieldValues>;
};

export function getValidationProps<TFieldValues>(options: GetValidationPropsOptions<TFieldValues>): ValidationReturn {
  const { fieldName, isRequired, errors, includeFieldNameInError, rules: additionalRules } = options;

  const fieldError = getNestedError(errors, fieldName);
  const isError = isFieldError(fieldError);

  const rules: UseControllerProps["rules"] = {
    ...(isRequired && { required: getRequiredErrorMessage(includeFieldNameInError ? fieldName : undefined) }),
    ...additionalRules,
  };

  return {
    isRequired,
    validate: isError ? "error" : undefined,
    validMessage: isError ? fieldError.message : undefined,
    rules,
  };
}
